import { API_BASE_URL } from '../config/api';
import { auth } from '../firebase';

export interface AutomatedMessage {
  id: string;
  dietitianId: string;
  dietitianName: string;
  clientId?: string;
  clientName?: string;
  title: string;
  content: string;
  triggerType: string;
  intervalDays?: number;
  dayOfWeek?: number;
  sendHour: number;
  sendMinute: number;
  daysBefore?: number;
  inactiveDays?: number;
  customDate?: string;
  repeatYearly: boolean;
  isActive: boolean;
  lastSentAt?: string;
  nextSendAt?: string;
  totalSentCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface AutomatedMessageLog {
  id: string;
  automatedMessageId: string;
  clientId: string;
  clientName: string;
  messageContent: string;
  triggerType: string;
  isDelivered: boolean;
  isRead: boolean;
  sentAt: string;
  readAt?: string;
}

export interface AutomatedMessageStats {
  totalRules: number;
  activeRules: number;
  totalMessagesSent: number;
  messagesThisWeek: number;
  messagesThisMonth: number;
}

export interface CreateAutomatedMessageRequest {
  title: string;
  content: string;
  triggerType: string;
  clientId?: string;
  clientName?: string;
  intervalDays?: number;
  dayOfWeek?: number;
  sendHour?: number;
  sendMinute?: number;
  daysBefore?: number;
  inactiveDays?: number;
  customDate?: string;
  repeatYearly?: boolean;
}

export interface UpdateAutomatedMessageRequest {
  title?: string;
  content?: string;
  triggerType?: string;
  clientId?: string;
  clientName?: string;
  intervalDays?: number;
  dayOfWeek?: number;
  sendHour?: number;
  sendMinute?: number;
  daysBefore?: number;
  inactiveDays?: number;
  customDate?: string;
  repeatYearly?: boolean;
  isActive?: boolean;
}

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Kullanıcı giriş yapmamış');
  }
  return await user.getIdToken();
};

export const automatedMessageApi = {
  // Tüm kuralları getir
  getAll: async (): Promise<AutomatedMessage[]> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Otomatik mesaj kuralları alınamadı');
    }

    return await response.json();
  },

  // Belirli bir kuralı getir
  getById: async (id: string): Promise<AutomatedMessage> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Otomatik mesaj kuralı bulunamadı');
    }

    return await response.json();
  },

  // Danışana ait kuralları getir
  getByClient: async (clientId: string): Promise<AutomatedMessage[]> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/client/${clientId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Danışan otomatik mesaj kuralları alınamadı');
    }

    return await response.json();
  },

  // Yeni kural oluştur
  create: async (
    request: CreateAutomatedMessageRequest
  ): Promise<AutomatedMessage> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Otomatik mesaj kuralı oluşturulamadı');
    }

    return await response.json();
  },

  // Kuralı güncelle
  update: async (
    id: string,
    request: UpdateAutomatedMessageRequest
  ): Promise<AutomatedMessage> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Otomatik mesaj kuralı güncellenemedi');
    }

    return await response.json();
  },

  // Kuralı sil
  delete: async (id: string): Promise<void> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Otomatik mesaj kuralı silinemedi');
    }
  },

  // Kuralın aktif/pasif durumunu değiştir
  toggle: async (id: string): Promise<AutomatedMessage> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/${id}/toggle`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Kural durumu değiştirilemedi');
    }

    return await response.json();
  },

  // Kuralı şimdi gönder
  sendNow: async (id: string): Promise<{ message: string }> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/${id}/send-now`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Mesaj gönderilemedi');
    }

    return await response.json();
  },

  // Logları getir
  getLogs: async (limit: number = 50): Promise<AutomatedMessageLog[]> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/logs?limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Mesaj logları alınamadı');
    }

    return await response.json();
  },

  // Danışana ait logları getir
  getLogsByClient: async (
    clientId: string,
    limit: number = 50
  ): Promise<AutomatedMessageLog[]> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/logs/client/${clientId}?limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Danışan mesaj logları alınamadı');
    }

    return await response.json();
  },

  // Kurala ait logları getir
  getLogsByRule: async (
    ruleId: string,
    limit: number = 50
  ): Promise<AutomatedMessageLog[]> => {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/automatedmessages/logs/rule/${ruleId}?limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Kural mesaj logları alınamadı');
    }

    return await response.json();
  },

  // İstatistikleri getir
  getStats: async (): Promise<AutomatedMessageStats> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('İstatistikler alınamadı');
    }

    return await response.json();
  },

  // Manuel işleme tetikle (test için)
  processNow: async (): Promise<void> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/automatedmessages/process`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Mesaj işleme başlatılamadı');
    }
  },
};

// Tetikleyici tür isimleri
export const triggerTypeLabels: Record<string, string> = {
  interval: 'Periyodik',
  appointment_reminder: 'Randevu Hatırlatması',
  program_start: 'Program Başlangıcı',
  program_end: 'Program Bitişi',
  no_weight_log: 'Kilo Kaydı Yok',
  no_meal_log: 'Öğün Kaydı Yok',
  custom_date: 'Özel Tarih',
};

// Gün isimleri
export const dayOfWeekLabels: Record<number, string> = {
  0: 'Pazar',
  1: 'Pazartesi',
  2: 'Salı',
  3: 'Çarşamba',
  4: 'Perşembe',
  5: 'Cuma',
  6: 'Cumartesi',
};

// Tetikleyici türü için ikon
export const getTriggerTypeIcon = (triggerType: string): string => {
  switch (triggerType) {
    case 'interval':
      return '🔄';
    case 'appointment_reminder':
      return '📅';
    case 'program_start':
      return '🚀';
    case 'program_end':
      return '🏁';
    case 'no_weight_log':
      return '⚖️';
    case 'no_meal_log':
      return '🍽️';
    case 'custom_date':
      return '📆';
    default:
      return '📨';
  }
};

// Kural açıklaması oluştur
export const getRuleDescription = (rule: AutomatedMessage): string => {
  switch (rule.triggerType) {
    case 'interval':
      const minuteStr = rule.sendMinute ? rule.sendMinute.toString().padStart(2, '0') : '00';
      return `Her ${rule.intervalDays} günde bir, saat ${rule.sendHour}:${minuteStr}'da`;
    case 'appointment_reminder':
      return `Randevudan ${rule.daysBefore} gün önce`;
    case 'program_start':
      return `Program başlangıcından ${rule.daysBefore} gün önce`;
    case 'program_end':
      return `Program bitiminden ${rule.daysBefore} gün önce`;
    case 'no_weight_log':
      return `${rule.inactiveDays} gün kilo kaydı yapılmadığında`;
    case 'no_meal_log':
      return `${rule.inactiveDays} gün öğün kaydı yapılmadığında`;
    case 'custom_date':
      const date = rule.customDate
        ? new Date(rule.customDate).toLocaleDateString('tr-TR')
        : '';
      return rule.repeatYearly ? `Her yıl ${date} tarihinde` : `${date} tarihinde`;
    default:
      return '';
  }
};

